import { type FC, Fragment, type HTMLAttributes } from 'react'

import { InlineAvatarStack } from '@/components/inline-avatar-stack/inline-avatar-stack'
import { BlockQuote } from '@/components/Text/BlockQuote'
import { Button } from '@/components/Button/Button'
import { Hairline } from '@/components/Hairline/Hairline'

import { PhoneFrame } from '@/components/PhoneFrame/PhoneFrame'
import type { Themes } from '@/Theme'

import { Text } from '@/components/Text/Text'
import { rewriteUrl as rewriteHelpCenterUrl } from '@/utils/helpCenterUrlRewriter'

import { HastAccordion } from './components/Accordion/Accordion'
import {
    Code,
    Em,
    Heading,
    Icon,
    IFrame,
    Image,
    Italic,
    List,
    Link,
    Note,
    Paragraph,
    Platforms,
    Pre,
    Space,
    Strong,
    Table,
} from './components/index'
import { isChildrenContainsIframe, isChildrenEmpty } from './utils/hast-sanitizers'

import type { Components } from '@doist/content-system'
import type { InlineAvatarProps } from '@/components/inline-avatar-stack/inline-avatar-stack'
import { Badge } from '@/components/Badge/Badge'
import type { NoteProps } from '@/components/HtmlArticle/components/note/note'

let isFirstImageProcessed = false

interface CustomComponents extends Components {
    platforms: FC<HTMLAttributes<HTMLElement>>
    icon: FC<HTMLAttributes<HTMLElement>>
    accordion: FC<HTMLAttributes<HTMLElement>>
    boxTip: FC<HTMLAttributes<HTMLElement>>
    boxNote: FC<HTMLAttributes<HTMLElement>>
    boxWarning: FC<HTMLAttributes<HTMLElement>>
    phoneFrame: FC<HTMLAttributes<HTMLElement>>
    img: FC<HTMLAttributes<HTMLImageElement>>
    inlineAvatar: FC<HTMLAttributes<HTMLElement>>
    fragment: FC<HTMLAttributes<HTMLElement>>
}

function helpCenterComponents(
    selectedPlatform: string | undefined,
    setSelectedPlatform: (platform: string) => void,
    theme: Themes = 'todoist',
): CustomComponents {
    return {
        fragment: (props) => <>{props.children}</>,
        p: (props) => {
            if (isChildrenEmpty(props.children)) {
                return <Fragment />
            }

            // iframes are contained in `<p>` (enforced by Zendesk formatting) but
            // our `IFrame` component is already surrounding the iframe with a `<div>`,
            // causing hydration errors. Skip rendering `<p>` and directly the sub-components.
            if (isChildrenContainsIframe(props.children)) {
                return <>{props.children}</>
            }

            return <Paragraph {...props} />
        },
        h1: (props) => <Heading tag="h1" theme={theme} {...props} />,
        h2: (props) => <Heading tag="h2" theme={theme} {...props} />,
        h4: (props) => <Heading tag="h4" theme={theme} {...props} />,
        h3: (props) => <Heading tag="h3" theme={theme} {...props} />,
        h5: (props) => <Heading tag="h5" theme={theme} {...props} />,
        a: (props) => <Link {...props} />,
        inlineAvatar: (props) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const { urls, doisters } = props

            if (urls) {
                return (
                    <InlineAvatarStack>
                        {urls.split(' ').map((url: string) => (
                            <InlineAvatarStack.Avatar key={url} src={url} />
                        ))}
                    </InlineAvatarStack>
                )
            }

            if (doisters) {
                return (
                    <InlineAvatarStack>
                        {doisters.split(' ').map((doister: InlineAvatarProps['doister']) => (
                            <InlineAvatarStack.Avatar key={doister} doister={doister} />
                        ))}
                    </InlineAvatarStack>
                )
            }

            return null
        },
        li: (props) => <Text tag="li" {...props} />,
        ul: (props) => <List tag="ul" {...props} />,
        ol: (props) => <List tag="ol" {...props} />,
        blockquote: (props) => <BlockQuote className="mbs-32" {...props} />,
        iframe: (props) => <IFrame {...props} />,
        pre: (props) => <Pre {...props} />,
        code: (props) => <Code {...props} />,
        strong: (props) => <Strong {...props} />,
        em: (props) => <Em {...props} />,
        br: (props) => <Space {...props} />,
        i: (props) => <Italic {...props} />,
        table: (props) => <Table {...props}>{props.children}</Table>,
        hr: (props) => <Hairline spacing={64} {...props} />,
        img: (props) => {
            const shouldLazyLoad = isFirstImageProcessed
            isFirstImageProcessed = true

            return <Image lazyload={shouldLazyLoad} {...props} />
        },
        button: (props) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const href = props.href ? rewriteHelpCenterUrl(props.href) : undefined

            return (
                <Button href={href} style={{ marginBlockStart: 'var(--space-8)' }} {...props}>
                    {props.children}
                </Button>
            )
        },

        // Custom components
        platforms: (props) => {
            return (
                <Platforms
                    selectedPlatform={selectedPlatform}
                    onSelectPlatform={setSelectedPlatform}
                >
                    {props.children}
                </Platforms>
            )
        },
        icon: (props) => {
            return <Icon {...props} />
        },
        accordion: (props) => {
            return (
                <HastAccordion theme={theme} {...props}>
                    {props.children}
                </HastAccordion>
            )
        },
        boxTip: ({ children, ...props }) => {
            return (
                <NoteWithBadge variant="tip" {...props}>
                    {children}
                </NoteWithBadge>
            )
        },
        boxNote: ({ children, ...props }) => {
            return (
                <NoteWithBadge variant="note" {...props}>
                    {children}
                </NoteWithBadge>
            )
        },
        boxWarning: ({ children, ...props }) => {
            return (
                <NoteWithBadge variant="warning" {...props}>
                    {children}
                </NoteWithBadge>
            )
        },
        phoneFrame: (props) => <PhoneFrame {...props} />,
    }
}

function NoteWithBadge({ variant, children, ...props }: NoteProps) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { badgeText, badgeVariant, ...rest } = props
    const badge = badgeText && badgeVariant && <Badge variant={badgeVariant}>{badgeText}</Badge>

    return (
        <Note variant={variant} badge={badge} {...rest}>
            {children}
        </Note>
    )
}
export { helpCenterComponents }
